import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import { toast } from 'react-toastify';
import api from '../services/api';

const AlumniSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [profile, setProfile] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    telephone: '',
    profile_image: null
  });
  const [profileImagePreview, setProfileImagePreview] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('alumni_token');
    if (!token) {
      navigate('/alumni/login');
      return;
    }
    fetchProfile();
  }, [navigate]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('alumni_token');
      const response = await api.get('/alumni/profile', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setProfile(response.data.profile);
        setFormData({
          full_name: response.data.profile.full_name || '',
          email: response.data.profile.email || '',
          telephone: response.data.profile.telephone || '',
          profile_image: null
        });
        if (response.data.profile.profile_image) {
          setProfileImagePreview(response.data.profile.profile_image);
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Image size should be less than 2MB');
        return;
      }
      setFormData({ ...formData, profile_image: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUpdating(true);
    
    try {
      const token = localStorage.getItem('alumni_token');
      const updateData = new FormData();
      updateData.append('full_name', formData.full_name);
      updateData.append('email', formData.email);
      updateData.append('telephone', formData.telephone);
      if (formData.profile_image) {
        updateData.append('profile_image', formData.profile_image);
      }
      
      const response = await api.put('/alumni/profile', updateData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        toast.success('Profile updated successfully!');
        // Update local storage user data
        const storedUser = JSON.parse(localStorage.getItem('alumni_user') || '{}');
        storedUser.name = formData.full_name;
        localStorage.setItem('alumni_user', JSON.stringify(storedUser));
        fetchProfile();
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600 mt-1">Manage your personal information</p>
      </div>

      <Card className="p-6">
        <form onSubmit={handleSubmit}>
          {/* Profile Image Upload */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Profile Picture
            </label>
            <div className="flex items-center gap-6">
              <div className="w-24 h-24 rounded-full bg-gray-200 overflow-hidden flex items-center justify-center">
                {profileImagePreview ? (
                  <img 
                    src={profileImagePreview} 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-4xl text-gray-400">📷</span>
                )}
              </div>
              <div>
                <label className="cursor-pointer bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 inline-flex items-center gap-2">
                  <span>📸</span>
                  Upload Photo
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </label>
                <p className="text-xs text-gray-500 mt-2">Max size: 2MB. JPG, PNG or GIF</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name
              </label>
              <Input
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mobile Number
              </label>
              <Input
                type="tel"
                value={formData.telephone}
                onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                required
              />
              <p className="text-xs text-gray-500 mt-1">This is your username for login</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Admission Number (Read-only)
              </label>
              <Input
                type="text"
                value={profile?.admin_number || ''}
                disabled
                className="bg-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Certificate Number
              </label>
              <Input
                type="text"
                value={profile?.certificate_number || 'Pending'}
                disabled
                className="bg-gray-100"
              />
              <p className="text-xs text-gray-500 mt-1">Certificate number will be assigned after verification</p>
            </div>
          </div>

          <div className="mt-6 flex gap-3">
            <Button type="submit" disabled={updating}>
              {updating ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button type="button" variant="outline" onClick={() => fetchProfile()}>
              Cancel
            </Button>
          </div>
        </form>

        <div className="mt-8 pt-6 border-t">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Account Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Membership Number:</span>
              <span className="ml-2 font-semibold text-primary">{profile?.alumni_membership_number || 'Pending'}</span>
            </div>
            <div>
              <span className="text-gray-500">Account Status:</span>
              <span className={`ml-2 font-semibold ${profile?.is_active ? 'text-green-600' : 'text-red-600'}`}>
                {profile?.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Member Since:</span>
              <span className="ml-2">{profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : 'N/A'}</span>
            </div>
            <div>
              <span className="text-gray-500">Last Login:</span>
              <span className="ml-2">{profile?.last_login ? new Date(profile.last_login).toLocaleDateString() : 'Never'}</span>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AlumniSettings;
